import { useState } from 'react';
import { ImageIcon, PlayCircleIcon, TrashIcon } from '@phosphor-icons/react';
import { UIVideoUploadDialog } from '@/components/dialogs/ui-video-upload-dialog';
import { FieldHint, SectionTitle } from '@/components/ui/text';
import { TextButton } from '@/components/ui/text-button';
import { CoverImageTile } from '../CoverImageTile';
import { PillButton } from '../PillButton';
import { FIELD_ERROR_MESSAGES, type FieldKey } from '../validation';
import { FieldError, RequiredMark } from '../FieldMarks';
import type { CoverEntry, PublishFlowImagesApi } from '../usePublishFlowImages';
import { ImageButton } from '@/components/ui/image-button';

interface Props {
  coverImages: PublishFlowImagesApi;
  projectInstanceId?: string | null;
  sceneOriginId?: string | null;
  errors: Set<FieldKey>;
}

const MAX_EXTRA_IMAGES = 3;

const EMPTY_ENTRY: CoverEntry = { id: null, url: null, originalUrl: null, crop: null };

export function TemplateMediaStep({ coverImages, projectInstanceId, sceneOriginId, errors }: Props) {
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const { templateVideoUrl, templateLandscapes, templateCoverOwn, sceneLandscape } = coverImages;

  // The template's cover is always its own row. Reusing the story's is a copy the
  // creator asks for, so a later edit to the story cover leaves the template alone.
  const canUseStoryCover = !templateCoverOwn && Boolean(sceneLandscape.url);

  const applyStoryCover = async () => {
    if (!sceneLandscape.originalUrl) return;
    setCopyError(null);
    setCopying(true);
    try {
      await coverImages.saveTemplateCover({
        imageOriginalUrl: sceneLandscape.originalUrl,
        imageCroppedUrl: sceneLandscape.url ?? undefined,
        crop: sceneLandscape.crop ?? undefined,
      });
    } catch {
      setCopyError('Could not reuse the story cover. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  const extraSlots = Array.from({ length: MAX_EXTRA_IMAGES }, (_, i) => templateLandscapes[i] ?? null);

  return (
    <div className="grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line h-full">
      <div className="space-y-3 min-w-0 pr-7">
        <SectionTitle>
          Video
          <RequiredMark />
        </SectionTitle>

        <ImageButton
          label={templateVideoUrl ? 'Replace template video' : 'Upload template video'}
          emptyLabel="Click to upload video"
          invalid={errors.has('templateVideo')}
          onClick={() => setVideoDialogOpen(true)}
          className="aspect-video w-full"
        >
          {templateVideoUrl ? (
            <>
              <video src={templateVideoUrl} className="absolute inset-0 h-full w-full object-cover" muted playsInline preload="metadata" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-lureo-emphasis/40 text-lureo-on-emphasis">
                  <PlayCircleIcon className="h-7 w-7" weight="fill" />
                </span>
              </span>
            </>
          ) : undefined}
        </ImageButton>

        <div className="flex items-start justify-between gap-3">
          <FieldHint>
            A short clip of your scene in AR. Landscape is best.
          </FieldHint>
          {templateVideoUrl && (
            <TextButton tone="destructive" size="sm" onClick={() => void coverImages.removeTemplateVideo()} className="mt-1.5 shrink-0">
              <TrashIcon />
              Remove
            </TextButton>
          )}
        </div>
        {errors.has('templateVideo') && <FieldError>{FIELD_ERROR_MESSAGES.templateVideo}</FieldError>}
      </div>

      <div className="space-y-4 min-w-0 pl-7">
        <SectionTitle>Cover and extra images</SectionTitle>

        <CoverImageTile
          aspect="16/9"
          fullWidth
          label="Template cover · Landscape (16:9)"
          category="TemplateLandscape"
          entry={templateCoverOwn ?? EMPTY_ENTRY}
          displayUrl={templateCoverOwn?.url ?? null}
          error={errors.has('templateCover')}
          projectInstanceId={projectInstanceId}
          sceneOriginId={sceneOriginId}
          onSave={(payload) => coverImages.saveTemplateCover(payload)}
          onRemove={() => coverImages.removeTemplateCover()}
        />
        {errors.has('templateCover') && <FieldError>{FIELD_ERROR_MESSAGES.templateCover}</FieldError>}

        {canUseStoryCover ? (
          <div className="flex items-center justify-between gap-2">
            <FieldHint>Click to upload, or reuse the cover from your story.</FieldHint>
            <PillButton
              icon={ImageIcon}
              label={copying ? 'Applying…' : 'Use story cover image'}
              loading={copying}
              onClick={() => void applyStoryCover()}
            />
          </div>
        ) : (
          <FieldHint>
            {templateCoverOwn
              ? 'This cover belongs to the template, so editing your story cover leaves it alone.'
              : 'Click to upload the cover the gallery shows for this template.'}
          </FieldHint>
        )}
        {copyError && <FieldHint>{copyError}</FieldHint>}

        <div className="grid grid-cols-3 gap-2">
          {extraSlots.map((slot, i) => (
            <CoverImageTile
              key={i}
              aspect="16/9"
              fullWidth
              label={`Extra ${i + 1}`}
              category="TemplateLandscape"
              entry={slot ?? EMPTY_ENTRY}
              displayUrl={slot?.url ?? null}
              projectInstanceId={projectInstanceId}
              sceneOriginId={sceneOriginId}
              onSave={(payload) => coverImages.saveTemplateLandscape(slot?.imageId ?? null, payload)}
              onRemove={() =>
                slot ? coverImages.removeTemplateLandscape(slot.imageId) : Promise.resolve()
              }
            />
          ))}
        </div>
        <FieldHint>
          Up to 3 extra landscape (16:9) images can sit alongside the cover in the gallery.
        </FieldHint>
      </div>

      <UIVideoUploadDialog
        open={videoDialogOpen}
        onOpenChange={setVideoDialogOpen}
        imageCategory="SceneVideo"
        contextId={sceneOriginId ?? undefined}
        currentVideo={templateVideoUrl ?? undefined}
        title="Upload template video"
        onVideoSelect={(videoUrl) => {
          if (videoUrl && videoUrl.trim() !== '') void coverImages.saveTemplateVideo(videoUrl);
          else void coverImages.removeTemplateVideo();
          setVideoDialogOpen(false);
        }}
      />
    </div>
  );
}
